import {
    Box,
    Button,
    Checkbox,
    Container,
    Field,
    Flex,
    Heading,
    Icon,
    Input,
    Link,
    Text,
    Textarea,
    Image,
} from "@chakra-ui/react"
import { useMutation, useQuery } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { useEffect, useState } from "react"
import { getAGB, handleError } from "../../utils"
import { IconType } from "react-icons/lib"
import { FiFacebook, FiInstagram, FiLinkedin, FiYoutube } from "react-icons/fi"
import { FaXTwitter } from "react-icons/fa6"
import { ApiError, FormsContactCreate, FormsContactService } from "../../client"
import useCustomToast from "../../hooks/useCustomToast"
import { useReCaptcha } from "../../hooks/useReCaptcha"
import { type SubmitHandler, useForm } from "react-hook-form"
import { PhoneNumberFormat, PhoneNumberUtil } from "google-libphonenumber"
import Logo from "../../../public/assets/images/logo.svg"
import HdLogo from "../../../public/assets/images/herrmann_druck.svg"
import { useSettings } from "../../components/UI/settings-context"
import NotFound from "../../components/Common/NotFound"
import { Helmet } from "react-helmet-async"
const ENVIRONMENT = import.meta.env.VITE_ENVIRONMENT

export const Route = createFileRoute("/_layout/kontakt")({
  component: Contact,
  notFoundComponent: NotFound,
})

type SocialLink = {
  href: string
  icon: IconType
}

function Contact() {
    // Social Links from policy
    const [socialLinks, setSocialLinks] = useState<SocialLink[]>([])
    const policy = useSettings()?.policy

    const [agbLink, setAgbLink] = useState<string | null>(null)
    const {data: agb} = useQuery({
        ...getAGB()
    })

    useEffect(() => {
        if(agb?.agb) {
        setAgbLink(agb.agb.path)
        }
    }, [agb])


    useEffect(() => {
        const links = []

        if(policy?.link_facebook) links.push({href: policy.link_facebook, icon: FiFacebook})
        if(policy?.link_instagram) links.push({href: policy.link_instagram, icon: FiInstagram})
        if(policy?.link_linkedin) links.push({href: policy.link_linkedin, icon: FiLinkedin})
        if(policy?.link_twitter) links.push({href: policy.link_twitter, icon: FaXTwitter})
        if(policy?.link_youtube) links.push({href: policy.link_youtube, icon: FiYoutube})

        setSocialLinks(links)
    }, [policy])

    // Contact form
    const siteKey = policy?.captcha_id ?? ""
    const { getToken } = useReCaptcha(siteKey, "contact_form")

    const showToast = useCustomToast()

    const phoneUtil = PhoneNumberUtil.getInstance()
     const PNF = PhoneNumberFormat

    const [agbChecked, setAgbChecked] = useState(false)

    const {
        register,
        handleSubmit,
        reset,
        formState: { errors, isSubmitting },
    } = useForm<FormsContactCreate>({
        mode: "onBlur",
        criteriaMode: "all",
        defaultValues: {
            firstname: "",
            lastname: "",
            email: "",
            phone: null,
            message: "",
        },
    })

    const mutation = useMutation({
        mutationFn: (data: { token: string; body: FormsContactCreate }) =>
            FormsContactService.createFormsContact({ recaptchaToken: data.token, requestBody: data.body }),
        onSuccess: () => {
            showToast("Versendet!", "Ihre Kontaktanfrage wurde versendet", "success")
            reset()
        },
        onError: (err: ApiError) => {
            handleError(err, showToast)
        },
    })

    const onSubmit: SubmitHandler<FormsContactCreate> = async (data) => {
        // Format phonenumber
        if(data.phone != null || data.phone != undefined) {
            if(data.phone == "") data.phone = null
            else {
                const number = phoneUtil.parseAndKeepRawInput(data.phone, "CH")
                data.phone = phoneUtil.format(number, PNF.RFC3966)
            }
        }

        const isTestEnv = ["local", "CI"].includes(ENVIRONMENT)

        // reCAPTCHA
        let token: string | null = "test-token"

        if (!isTestEnv) {
            token = await getToken()
            if(!token) {
                showToast("Fehler!", "reCAPTCHA konnte nicht ausgeführt werden", "error")
                return
            }
        }

        mutation.mutate({ token, body: data })
    }

    return (
        <>
            <Helmet>
                {policy?.captcha_id && <script src={`https://www.google.com/recaptcha/api.js?render=${policy.captcha_id}`} /> }
            </Helmet>

            <Container>
                <Flex direction={{base: "column", md: "row"}} mb={20}>
                    <Box w="full">
                        <Heading size="4xl" color="main" mb={8}>Kontakt</Heading>

                        <Heading size="md" mb={2} color="main">Wochen-Zeitung</Heading>
                        <Text mb={2}>Brennerstrasse 7</Text>
                        <Text mb={4}>3550 Langnau i. E.</Text>

                        <Text mb={2}>Tel.: <Link href="tel:+41 34 409 40 01" color="main">034 409 40 01</Link></Text>
                        <Text mb={8}>E-Mail: <Link href="mailto:info@wochen-zeitung.ch" color="main">info@wochen-zeitung.ch</Link></Text>

                        <Heading size="md" mb={2} color="main">Redaktion</Heading>
                        <Text mb={8}>Tel.: <Link href="tel:+41 34 409 40 05" color="main">034 409 40 05</Link></Text>

                        <Heading size="md" mb={2} color="main">Öffnungszeiten</Heading>
                        <Text mb={2}>Montag - Freitag</Text>
                        <Text mb={2}>07:30 - 12:00 Uhr</Text>
                        <Text mb={8}>13:30 - 17:00 Uhr</Text>

                        <Flex wrap="wrap" data-testid="contact-socials">
                            {socialLinks?.map((social, i) => (
                                <Link
                                    key={i}
                                    href={social.href}
                                    aria-label={social.href}
                                    target="_blank"
                                    color="main"
                                    mx={5}
                                    my={2}
                                    p={2}
                                    border="2px solid"
                                    borderColor="secondary"
                                    borderRadius="full"
                                    backgroundColor="secondary"
                                    _hover={{borderColor: "main"}}
                                >
                                    <Icon size="md" as={social.icon} />
                                </Link>
                            ))}
                        </Flex>
                    </Box>
                    <Box
                        display="flex"
                        flexDir="column"
                        alignItems={{base: "center", md: "flex-end"}}
                        w="full"
                        my={10}
                    >
                        <Box pos="relative" w="15em" h="20em" right={0}>
                            <Box
                                backgroundColor="white"
                                border="2px solid"
                                borderColor="main"
                                opacity={0.8}
                                p={5}
                                w="15em"
                                h="15em"
                                borderRadius="full"
                                pos="absolute"
                                alignContent="center"
                                display="flex"
                                justifyContent="center"
                                alignItems="center"
                            >
                                <Link
                                    href="https://www.wochen-zeitung.ch/"
                                    target="_blank"
                                    h="inherit"
                                    w="inherit"
                                >
                                    <Image
                                        src={policy?.logo ? policy.logo.path.replace(/-(LG)\./, "-MD.") : Logo}
                                        alt="Logo"
                                        data-testid="circle-logo-first"
                                    />
                                </Link>
                            </Box>

                            <Box
                                backgroundColor="white"
                                border="2px solid"
                                borderColor="main"
                                opacity={0.8}
                                p={5}
                                w="10em"
                                h="10em"
                                borderRadius="full"
                                pos="absolute"
                                alignContent="center"
                                top="10em"
                                left="-4em"
                                display="flex"
                                justifyContent="center"
                                alignItems="center"
                            >
                                <Link
                                    href="https://www.herrmann-druck.ch/"
                                    target="_blank"
                                    h="inherit"
                                    w="inherit"
                                >
                                    <Image
                                        src={HdLogo}
                                        alt="Herrmann-Logo"
                                        data-testid="circle-logo-second"
                                    />
                                </Link>
                            </Box>
                        </Box>
                    </Box>
                </Flex>

                <Heading size="2xl" mb={6} color="main">Kontaktformular</Heading>

                <form onSubmit={handleSubmit(onSubmit)}>
                    <Field.Root required invalid={!!errors.lastname}>
                        <Field.Label htmlFor="lastname">
                        Name
                        <Field.RequiredIndicator />
                        </Field.Label>
                        <Input
                        id="lastname"
                        {...register("lastname", {
                            required: "Name ist erforderlich",
                            maxLength: {
                                value: 255,
                                message: "Name darf maximal 255 Zeichen lang sein",
                            },
                        })}
                        placeholder="Name"
                        type="text"
                        />
                        {errors.lastname && (
                        <Field.ErrorText>{errors.lastname.message}</Field.ErrorText>
                        )}
                    </Field.Root>

                    <Field.Root required invalid={!!errors.firstname} mt={6}>
                        <Field.Label htmlFor="firstname">
                        Vorname
                        <Field.RequiredIndicator />
                        </Field.Label>
                        <Input
                        id="firstname"
                        {...register("firstname", {
                            required: "Vorname ist erforderlich",
                            maxLength: {
                                value: 255,
                                message: "Vorname darf maximal 255 Zeichen lang sein",
                            },
                        })}
                        placeholder="Vorname"
                        type="text"
                        />
                        {errors.firstname && (
                        <Field.ErrorText>{errors.firstname.message}</Field.ErrorText>
                        )}
                    </Field.Root>

                    <Field.Root required invalid={!!errors.email} mt={6}>
                        <Field.Label htmlFor="email">
                        E-Mail
                        <Field.RequiredIndicator />
                        </Field.Label>
                        <Input
                        id="email"
                        {...register("email", {
                            required: "E-Mail ist erforderlich",
                            maxLength: {
                                value: 255,
                                message: "E-Mail darf maximal 255 Zeichen lang sein",
                            },
                        })}
                        placeholder="E-Mail"
                        type="email"
                        />
                        {errors.email && (
                        <Field.ErrorText>{errors.email.message}</Field.ErrorText>
                        )}
                    </Field.Root>

                    <Field.Root invalid={!!errors.phone} mt={6}>
                        <Field.Label htmlFor="phone">
                        Telefonnummer
                        </Field.Label>
                        <Input
                        id="phone"
                        {...register("phone", {
                            maxLength: {
                                value: 25,
                                message: "Telefonnummer darf maximal 25 Zeichen lang sein",
                            },
                        })}
                        placeholder="Telefonnummer"
                        type="tel"
                        />
                        {errors.phone && (
                        <Field.ErrorText>{errors.phone.message}</Field.ErrorText>
                        )}
                    </Field.Root>

                    <Field.Root required invalid={!!errors.message} mt={6}>
                        <Field.Label htmlFor="message">
                        Mitteilung
                        <Field.RequiredIndicator />
                        </Field.Label>

                        <Textarea
                            id="message"
                            {...register("message", {
                                required: "Mitteilung ist erforderlich",
                                maxLength: {
                                    value: 1000,
                                    message: "Mitteilung darf maximal 1000 Zeichen lang sein",
                                },
                            })}
                            placeholder="Mitteilung"
                        />

                        {errors.message && (
                        <Field.ErrorText>{errors.message.message}</Field.ErrorText>
                        )}
                    </Field.Root>

                    <Field.Root mt={6}>
                        <Checkbox.Root
                            required
                            checked={agbChecked}
                            onCheckedChange={(e) => setAgbChecked(!!e.checked)}
                        >
                            <Checkbox.HiddenInput />
                            <Checkbox.Control _checked={{backgroundColor: "main"}} borderColor="main" />
                            <Checkbox.Label>Ich akzeptiere die <Link color="main" href={agbLink ?? "/"} target="_blank">Allgemeinen Geschäftsbedingungen (AGB)</Link></Checkbox.Label>
                        </Checkbox.Root>
                    </Field.Root>

                    <Flex mt={6}>
                        <Text color="dim" fontSize="sm">Diese Website ist durch reCAPTCHA geschützt.</Text>
                    </Flex>

                    <Button
                        mt={10}
                        variant="primary"
                        textTransform="uppercase"
                        type="submit"
                        loading={isSubmitting}
                        disabled={!agbChecked}
                    >
                        Absenden
                    </Button>
                </form>
            </Container>
        </>
    )
}
